import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject, filter, take } from 'rxjs';

/** State of the dialog container animation. */
export type DialogState = 'void' | 'enter' | 'exit';

/**
 * Reference to an opened dialog. Provides a handle to control
 * and observe the dialog lifecycle.
 */
export class DialogRef<T = unknown, R = unknown> {
  /** The component instance rendered inside the dialog. */
  componentInstance!: T;

  /** Whether the user disabled closing the dialog via backdrop click or Escape. */
  disableClose: boolean;

  /** Unique ID for this dialog instance. */
  readonly id: string;

  private readonly afterOpenedSubject = new Subject<void>();
  private readonly afterClosedSubject = new Subject<R | undefined>();
  private readonly beforeClosedSubject = new Subject<R | undefined>();
  private result: R | undefined;
  private closed = false;

  /** Current animation state of the dialog container. */
  containerState: DialogState = 'enter';

  /** Callback invoked by the container when exit animation finishes. */
  onAnimationDone: (() => void) | null = null;

  constructor(
    private readonly overlayRef: OverlayRef,
    id: string,
    disableClose = false
  ) {
    this.id = id;
    this.disableClose = disableClose;
  }

  /** Emits after the dialog has been fully opened (enter animation complete). */
  afterOpened(): Observable<void> {
    return this.afterOpenedSubject.asObservable();
  }

  /** Emits when the dialog finishes closing. */
  afterClosed(): Observable<R | undefined> {
    return this.afterClosedSubject.asObservable();
  }

  /** Emits before the dialog starts closing. */
  beforeClosed(): Observable<R | undefined> {
    return this.beforeClosedSubject.asObservable();
  }

  /** Emits when the user clicks the backdrop. */
  backdropClick(): Observable<MouseEvent> {
    return this.overlayRef.backdropClick();
  }

  /** Emits when a keyboard event targets the overlay. */
  keydownEvents(): Observable<KeyboardEvent> {
    return this.overlayRef.keydownEvents();
  }

  /** Close the dialog with an optional result value. */
  close(result?: R): void {
    if (this.closed) {
      return;
    }

    this.result = result;
    this.beforeClosedSubject.next(result);
    this.beforeClosedSubject.complete();

    this.containerState = 'exit';

    this.onAnimationDone = () => {
      this.finishClose();
    };
  }

  /** @internal Notify that the dialog has finished opening. */
  _markOpened(): void {
    this.afterOpenedSubject.next();
    this.afterOpenedSubject.complete();
  }

  /** @internal Force close without animation (used by service on destroy). */
  _finishImmediately(): void {
    if (!this.closed) {
      this.result = undefined;
      this.finishClose();
    }
  }

  private finishClose(): void {
    this.closed = true;
    this.overlayRef.dispose();
    this.afterClosedSubject.next(this.result);
    this.afterClosedSubject.complete();
    this.onAnimationDone = null;
  }
}
